// src/utils/fetchInterceptor.ts
// Global fetch interceptor that catches 401s on GraphQL calls and
// attempts token refresh + retry. Installed once at app startup so
// all 194+ raw fetch() calls get automatic refresh handling.

import { getAuthToken } from './cookieUtils';
import {
  refreshAccessToken,
  clearAuthState,
} from '../services/authService';

const originalFetch = window.fetch;

let isRedirecting = false;

window.fetch = async function interceptedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const response = await originalFetch.call(window, input, init);

  // Only intercept GraphQL API calls
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  if (!url.includes('/api/graphql')) {
    return response;
  }

  if (response.status !== 401) {
    return response;
  }

  // Don't retry if we're already redirecting to login
  if (isRedirecting) {
    return response;
  }

  // Attempt refresh
  try {
    const newToken = await refreshAccessToken();

    // Rebuild the request with the new token
    const retryHeaders = new Headers(init?.headers || {});
    retryHeaders.set('Authorization', `Bearer ${newToken}`);

    return originalFetch.call(window, input, {
      ...init,
      headers: retryHeaders,
    });
  } catch {
    // Refresh failed — clear auth and redirect to login
    isRedirecting = true;
    clearAuthState();
    window.location.href = '/login';
    return response;
  }
};
